import { useState } from 'react'

import { Avatar, Divider, Tooltip, UnstyledButton } from '@mantine/core'

import { routes, useLocation } from '@redwoodjs/router'

import Header from 'src/components/Navigation/Header/Header'
import SectionNav from 'src/components/Navigation/SectionNav/SectionNav'
import type { SectionNavItem } from 'src/components/Navigation/SectionNav/SectionNav'
import SidebarLink from 'src/components/Navigation/SidebarLink/SidebarLink'
import DashboardContexts from 'src/context/DashboardContexts'
import { Logo, Name } from 'src/icons'
import IconDashboard from 'src/icons/IconDashboard'
import IconEmployment from 'src/icons/IconEmployment'
import IconPuzzle from 'src/icons/IconPuzzle'
import IconRightArrows from 'src/icons/IconRightArrows'
import IconShield from 'src/icons/IconShield'

type AdminLayoutProps = {
  children?: React.ReactNode
}

type AdminNavItem = {
  label: string
  route: string
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  title?: string
  prefix?: string
  section?: SectionNavItem[]
}

const ADMIN_VOE_PREFIX = '/admin/voe'
const ADMIN_INTEGRATION_PREFIX = '/admin/integration'

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { pathname } = useLocation()
  const navItems: AdminNavItem[] = [
    {
      label: 'Admin Dashboard',
      route: routes.adminHome(),
      icon: IconDashboard,
    },
    {
      label: 'Employment Verifications',
      route: routes.adminVoeCarriers(),
      icon: IconEmployment,
      prefix: ADMIN_VOE_PREFIX,
      section: [
        {
          label: 'Carriers',
          route: routes.adminVoeCarriers(),
          matchPrefix: `${ADMIN_VOE_PREFIX}/carriers`,
        },
        { label: 'Contacts', route: routes.adminVoeContacts() },
        { label: 'Suppressed', route: routes.adminVoeSuppressed() },
      ],
    },
    {
      label: 'Integration',
      route: routes.adminIntegrationApiClients(),
      icon: IconPuzzle,
      prefix: ADMIN_INTEGRATION_PREFIX,
      section: [
        {
          label: 'API Clients',
          route: routes.adminIntegrationApiClients(),
        },
      ],
    },
  ]
  const active = navItems.find((item) =>
    item.prefix
      ? pathname === item.prefix || pathname.startsWith(`${item.prefix}/`)
      : item.route === pathname
  )
  const title = active?.title ?? active?.label ?? 'Admin Dashboard'

  const expanded = localStorage.getItem('adminSidebarExpanded')
  const [sidebarExpanded, setSidebarExpanded] = useState(expanded !== 'false')
  const [showSidebarOnMobile, setShowSidebarOnMobile] = useState(false)

  const toggleSidebar = () =>
    setSidebarExpanded((previous) => {
      localStorage.setItem('adminSidebarExpanded', String(!previous))
      return !previous
    })

  return (
    <DashboardContexts>
      <div className="flex h-screen flex-row overflow-y-hidden bg-doubleNickel-gray-100">
        <div
          className={`flex h-full flex-col items-center bg-doubleNickel-gray-100 px-2 pb-1.5 pt-2 transition-all duration-700 ease-in-out
          ${sidebarExpanded ? 'w-[13.25rem]' : 'w-[4rem]'}
          ${!showSidebarOnMobile && 'hidden sm:flex'} `}
        >
          <Tooltip
            label="Double Nickel Admin"
            position="right"
            disabled={sidebarExpanded}
          >
            <div
              className={`flex h-[7%] min-h-12 w-full flex-row items-center justify-center rounded-lg py-2 ${
                sidebarExpanded ? 'bg-white px-2 shadow' : ''
              }`}
            >
              <div
                className={`flex flex-row items-center gap-2 ${
                  sidebarExpanded ? 'w-full' : ''
                }`}
              >
                <div
                  aria-label="Double Nickel Admin"
                  role="img"
                  className="flex h-8 w-8 flex-none items-center justify-center rounded-[10px] bg-doubleNickel-brand-500"
                >
                  <IconShield className="h-[18px] w-[18px] fill-none stroke-white" />
                </div>
                {sidebarExpanded && (
                  <div className="flex flex-col leading-tight">
                    <div className="text-sm font-semibold text-doubleNickel-gray-900">
                      Double Nickel
                    </div>
                    <div className="text-xs text-doubleNickel-gray-500">
                      Admin
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Tooltip>

          <div className="flex flex-col gap-1.5">
            <div className="h-3">{!sidebarExpanded && <Divider />}</div>
            {navItems.map((item) => (
              <SidebarLink
                key={item.label}
                label={item.label}
                route={item.route}
                icon={item.icon}
                expanded={sidebarExpanded}
                matchExact
              />
            ))}
          </div>

          <div className="mt-auto flex w-full flex-col items-center gap-4">
            <UnstyledButton
              onClick={toggleSidebar}
              aria-label={
                sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'
              }
              className={`mb-4 ${sidebarExpanded ? 'ml-auto' : ''}`}
            >
              <IconRightArrows
                className={`h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500 transition-all duration-700 ease-in-out ${
                  sidebarExpanded ? 'rotate-180' : ''
                }`}
              />
            </UnstyledButton>
          </div>

          <Tooltip
            label="Double Nickel"
            position="right"
            disabled={sidebarExpanded}
          >
            <div
              className={`mb-4 flex flex-row items-center justify-center p-1.5 ${
                sidebarExpanded && 'gap-2'
              }`}
            >
              <Avatar src={Logo} radius="sm" size={32} alt="Double Nickel" />
              <img
                src={Name}
                alt="Double Nickel"
                className={`transition-all duration-700 ease-in-out ${
                  sidebarExpanded ? 'h-8' : 'h-0'
                }`}
              />
            </div>
          </Tooltip>
        </div>

        <div className="m-2 flex flex-1 flex-col gap-1 overflow-hidden rounded-lg">
          <Header
            showSidebarOnMobile={showSidebarOnMobile}
            setShowSidebarOnMobile={setShowSidebarOnMobile}
            showSearch={false}
            showNotifications={false}
            title={title}
          />
          <div className="h-[93%] overflow-auto rounded-lg bg-white pt-5">
            {active?.section ? (
              <div className="flex h-full flex-row gap-6 px-6">
                <SectionNav heading={active.label} items={active.section} />
                <div className="min-w-0 flex-1">{children}</div>
              </div>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </DashboardContexts>
  )
}

export default AdminLayout
